import clsx from "clsx";
import { FC, useCallback, useContext, useEffect, useState } from "react";

import {
  GameLogContext,
  isMessage,
  useAwaitMessageBroker,
} from "@dndbeyond/game-log-components";
import type { AnyMessage } from "@dndbeyond/message-broker-lib";
import { Rollable } from "@dndbeyond/pocket-dimension-dice/components/Rollable";
import type { RollableProps } from "@dndbeyond/pocket-dimension-dice/components/Rollable";
import "@dndbeyond/pocket-dimension-dice/components/Rollable/index.css";
import { DiceVisibilities } from "@dndbeyond/pocket-dimension-dice/constants";
import { useUserDiceDataStore } from "@dndbeyond/pocket-dimension-dice/hooks/useUserDiceDataStore";
import { RollDicePayload } from "@dndbeyond/pocket-dimension-dice/types";

export interface DigitalDiceWrapperProps
  extends Omit<RollableProps, "dmId" | "gameId"> {
  rollCallback?: (rollData: RollDicePayload["data"]) => void;
  isDiceEnabled?: boolean;
}

export const DigitalDiceWrapper: FC<DigitalDiceWrapperProps> = ({
  isContextMenuEnabled = true,
  rollType,
  children,
  diceNotation,
  isDiceEnabled = true,
  action,
  rollKind,
  entityId,
  entityType,
  name,
  rollCallback,
}) => {
  const [{ activeCampaign, userId }] = useContext(GameLogContext);
  const [rollData, setRollData] = useState<RollDicePayload["data"]>();
  const mb = useAwaitMessageBroker();
  const handleMessage = useCallback(
    (message: AnyMessage) => {
      if (
        isMessage(message) &&
        message.eventType === "dice/roll/fulfilled" &&
        message.data.rollId === rollData?.rollId
      ) {
        rollCallback?.(rollData!);
        setRollData(undefined);
      }
    },
    [rollCallback, rollData]
  );

  // Only subscribe when there's a pending roll to listen for
  useEffect(() => {
    if (!mb || !rollCallback || !rollData) {
      return;
    }

    return mb.subscribe(handleMessage);
  }, [mb, rollCallback, rollData, handleMessage]);

  // `rollDice` dispatches `dice/roll/fulfilled` synchronously, before this
  // component has a chance to subscribe to it (the subscription above only
  // attaches once `rollData` is set, one render later), whenever either: 3D
  // dice visibility is disabled, or the roll has no actual dice in it (a
  // flat/static roll, e.g. unarmed strike's fixed damage). In both cases
  // `dice/roll/deferred` is never sent and the message broker never replays
  // the missed `fulfilled` message, so `rollCallback` would otherwise never
  // run. Apply the result immediately instead of waiting on the message
  // broker whenever we can tell in advance that this roll will resolve
  // synchronously, matching the pattern DiceRollGroup's handleRoll already
  // uses for the visibility case.
  //
  // `Rollable`'s `setRollData` prop is only ever invoked with the roll data
  // itself, never an updater function, so we don't need to support the
  // `SetStateAction` function form here.
  const hasDice = /\d+d\d+/.test(String(diceNotation));
  const handleRollData = useCallback(
    (data: RollDicePayload["data"]) => {
      const isVisibilityDisabled =
        useUserDiceDataStore.getState().getUserDiceData(Number(userId))
          ?.settings.visibility === DiceVisibilities.Disabled;

      if (isVisibilityDisabled || !hasDice) {
        rollCallback?.(data);
        return;
      }

      setRollData(data);
    },
    [rollCallback, userId, hasDice]
  );

  return (
    <Rollable
      className={clsx(isDiceEnabled && "integrated-dice__container")}
      diceNotation={String(diceNotation)}
      action={action}
      entityId={entityId}
      entityType={entityType}
      name={name}
      dmId={activeCampaign ? String(activeCampaign.dmId) : undefined}
      gameId={activeCampaign ? String(activeCampaign.id) : undefined}
      rollKind={rollKind}
      rollType={rollType}
      isContextMenuEnabled={isContextMenuEnabled}
      isDiceEnabled={isDiceEnabled}
      setRollData={handleRollData}
    >
      {children}
    </Rollable>
  );
};
